import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertTriangle, CheckCircle2, Circle, FlaskConical, Loader2, MapPin, TrendingUp } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { HAZARD_BASE, getHazard } from "@/lib/hazard";
import { ExposureBlock } from "@/components/ExposureBlock";
import { ImpactBlock } from "@/components/ImpactBadge";
import { CascadeChain } from "@/components/CascadeChain";
import { PlaybookBlock } from "@/components/PlaybookBlock";
import { TimelineBlock } from "@/components/TimelineBlock";
import { SatelliteImagery } from "@/components/SatelliteImagery";
import { SharedNote } from "@/components/HypothesisCard";

const STATUS = {
  unexplained_anomaly: { label: "UNEXPLAINED ANOMALY", color: "text-atlas-red", Icon: AlertTriangle },
  explained_anomaly: { label: "EXPLAINED ANOMALY", color: "text-atlas-orange", Icon: CheckCircle2 },
  nominal: { label: "NOMINAL", color: "text-atlas-cyan", Icon: Circle },
};

const Row = ({ k, v }) => (
  <div className="flex justify-between gap-3 py-1.5 border-b border-white/5 min-w-0">
    <span className="text-[10px] uppercase tracking-widest text-atlas-secondary/80 font-mono shrink-0">{k}</span>
    <span className="text-[11px] text-atlas-foreground font-mono text-right break-all min-w-0">{v}</span>
  </div>
);

const RegionTrend = ({ obs }) => {
  const trend = obs?.metrics?.trend;
  if (!Array.isArray(trend) || trend.length < 3) return null;
  const color = HAZARD_BASE[getHazard(obs)] || "#00F0FF";
  const fc = obs?.forecast;
  const data = trend.map((p, i) => ({ x: p.date ?? i, value: p.value, proj: null }));
  const showProj = fc && typeof fc.projected_value === "number";
  if (showProj && data.length) {
    data[data.length - 1].proj = data[data.length - 1].value; // anchor the dashed segment
    data.push({ x: "+72h", value: null, proj: fc.projected_value });
  }
  const projColor =
    fc?.outlook === "escalating" ? "#FF9500" : fc?.outlook === "watch" ? "#FBBF24" : fc?.outlook === "easing" ? "#34D399" : "#8B98A5";
  const fmt = (d) => (typeof d === "string" && d.length >= 10 ? d.slice(5) : d);
  return (
    <div className="mb-4" data-testid="region-trend">
      <div className="flex items-center gap-1.5 mb-2">
        <TrendingUp className="w-3.5 h-3.5 text-atlas-cyan" />
        <span className="text-[10px] uppercase tracking-widest text-atlas-cyan/60 font-mono">
          Region Focus · Trend + 72h Projection
        </span>
      </div>
      <div className="h-28 w-full border border-white/10 bg-atlas-void/60 p-1">
        <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 320, height: 112 }}>
          <LineChart data={data} margin={{ top: 6, right: 8, bottom: 0, left: -18 }}>
            <XAxis dataKey="x" tickFormatter={fmt} tick={{ fill: "#8B98A5", fontSize: 8 }} interval="preserveStartEnd" />
            <YAxis tick={{ fill: "#8B98A5", fontSize: 8 }} width={34} />
            <Tooltip
              contentStyle={{ background: "#0D1117", border: "1px solid rgba(255,255,255,0.15)", fontSize: 10, fontFamily: "monospace" }}
              labelStyle={{ color: "#8B98A5" }}
              labelFormatter={fmt}
            />
            <Line type="monotone" dataKey="value" stroke={color} strokeWidth={1.6} dot={false} isAnimationActive={false} connectNulls={false} />
            {showProj && (
              <Line type="monotone" dataKey="proj" stroke={projColor} strokeWidth={1.6} strokeDasharray="4 3" dot={{ r: 2, fill: projColor }} isAnimationActive={false} connectNulls />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="text-[9px] font-mono text-atlas-secondary/75 mt-1">
        {obs.unit}{showProj ? "  ·  dashed = 72h projection" : ""}
      </div>
    </div>
  );
};

const ForecastBlock = ({ forecast }) => {
  if (!forecast || forecast.outlook === "na") return null;
  const tone =
    forecast.outlook === "escalating"
      ? "text-atlas-orange border-atlas-orange/40 bg-atlas-orange/5"
      : forecast.outlook === "watch"
      ? "text-amber-400 border-amber-400/40 bg-amber-400/5"
      : forecast.outlook === "easing"
      ? "text-emerald-400 border-emerald-400/40 bg-emerald-400/5"
      : "text-atlas-secondary border-white/10 bg-white/[0.02]";
  return (
    <div className={`mb-4 border-l-2 p-3 ${tone}`} data-testid="forecast-block">
      <div className="flex items-center gap-1.5 mb-1">
        <TrendingUp className="w-3.5 h-3.5" />
        <span className="text-[10px] uppercase tracking-widest font-mono opacity-80">72-Hour Forecast</span>
      </div>
      <div className="text-sm leading-snug mb-1">{forecast.headline}</div>
      {forecast.detail && <p className="text-[11px] text-atlas-secondary leading-relaxed">{forecast.detail}</p>}
    </div>
  );
};

const HypBlock = ({ h }) => (
  <div className="border border-white/10 border-l-2 border-l-atlas-cyan p-2.5 mb-2 cyan-glow">
    <SharedNote hyp={h} />
    <div className="flex items-center justify-between mb-1">
      <span className="text-xs font-display text-white">{h.name}</span>
      {typeof h.calibrated_score === "number" ? (
        <span
          className="text-[9px] font-mono uppercase text-atlas-cyan text-right leading-tight"
          data-testid={`drawer-hypothesis-calibrated-${h.uid}`}
        >
          {Math.round(h.calibrated_score * 100)}% calibrated
          <span className="block text-[8px] text-atlas-secondary/75 normal-case">
            AI {Math.round(h.confidence_score * 100)}% · {h.calibrated_n > 0 ? `${h.calibrated_n} outcomes` : "prior"}
          </span>
        </span>
      ) : (
        <span className="text-[9px] font-mono uppercase text-atlas-cyan">
          {h.confidence} · {Math.round(h.confidence_score * 100)}%
        </span>
      )}
    </div>
    {h.plain_summary && (
      <div className="text-[12px] text-white/95 leading-snug bg-atlas-cyan/5 border-l-2 border-atlas-cyan/40 pl-2 py-1 mb-1.5">
        <span className="text-[8px] font-mono uppercase tracking-widest text-atlas-cyan/60 block">In plain English</span>
        {h.plain_summary}
      </div>
    )}
    <p className="text-[11px] text-atlas-secondary leading-relaxed mb-1.5">{h.statement}</p>
    <p className="text-[10px] text-atlas-secondary/85 mb-1.5">
      <span className="text-atlas-cyan/60 font-mono">MECHANISM · </span>
      {h.mechanism}
    </p>
    {h.suggested_tests?.length > 0 && (
      <div className="text-[10px] font-mono text-atlas-foreground/80">
        <span className="text-atlas-cyan/60">TEST · </span>
        {h.suggested_tests[0]}
      </div>
    )}
  </div>
);

export const DetailDrawer = ({ open, onOpenChange, detail, loading, error }) => {
  const obs = detail?.observation;
  const hyps = detail?.hypotheses || [];
  const event = detail?.event;
  const img = detail?.imagery;
  const meta = obs ? STATUS[obs.triage_status] || STATUS.nominal : STATUS.nominal;
  const p = obs?.provenance || {};
  const analyzing = obs?.triage_status === "unexplained_anomaly" && !obs?.has_hypothesis;

  const Col = ({ children }) => <div className="min-w-0 flex flex-col">{children}</div>;
  const Label = ({ children }) => (
    <div className="text-[10px] uppercase tracking-widest text-atlas-cyan/80 font-mono mb-1">{children}</div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        aria-describedby={undefined}
        className="w-[95vw] lg:w-[70vw] max-w-[95vw] lg:max-w-[70vw] h-[86vh] bg-atlas-surface border border-white/10 p-0 gap-0 text-atlas-foreground flex flex-col rounded-none"
        data-testid="detail-drawer"
      >
        <DialogHeader className="h-14 shrink-0 px-5 flex flex-row items-center justify-between border-b border-white/10 space-y-0">
          <DialogTitle className="flex items-center gap-2 text-white font-display text-base">
            <MapPin className="w-4 h-4 text-atlas-cyan" />
            Deep-Dive Provenance
            {obs && <span className="text-atlas-secondary font-mono text-xs ml-2">· {obs.region}</span>}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center flex-1">
            <Loader2 className="w-5 h-5 text-atlas-cyan animate-spin" />
          </div>
        ) : !obs ? (
          <div className="flex flex-col items-center justify-center flex-1 px-6 text-center gap-2" data-testid="detail-error">
            <AlertTriangle className="w-5 h-5 text-atlas-orange" />
            <span className="text-[11px] font-mono text-atlas-secondary/85 uppercase tracking-widest">
              {error || "Observation unavailable"}
            </span>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto p-5" data-testid="detail-body">
            <div className="flex items-baseline justify-between mb-4">
              <div>
                <div className="flex items-center gap-1.5 mb-1">
                  <meta.Icon className={`w-3.5 h-3.5 ${meta.color}`} />
                  <span className={`text-[10px] font-mono tracking-widest ${meta.color}`}>{meta.label}</span>
                </div>
                <h3 className="text-xl font-display text-white">{obs.region}</h3>
              </div>
              <span className={`text-2xl font-mono ${meta.color}`}>
                {obs.deviation_sigma >= 0 ? "+" : ""}
                {obs.deviation_sigma}σ
              </span>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <Col>
                <TimelineBlock obs={obs} testId={`drawer-timeline-${obs.uid}`} />
                <SatelliteImagery imagery={img} testId={`drawer-imagery-${obs.uid}`} />
                <ForecastBlock forecast={obs.forecast} />
                <RegionTrend obs={obs} />
              </Col>

              <Col>
                <div className="mb-4" data-testid="drawer-hypothesis">
                  <div className="flex items-center gap-1.5 mb-2">
                    <FlaskConical className="w-3.5 h-3.5 text-atlas-cyan" />
                    <span className="text-[10px] uppercase tracking-widest text-atlas-cyan font-mono">Vigilo Earth Hypothesis</span>
                  </div>
                  {hyps.length === 0 && analyzing && (
                    <div className="flex flex-col gap-2 animate-pulse" data-testid="drawer-hypothesis-loading">
                      <div className="h-3 w-2/3 bg-white/10" />
                      <div className="h-10 w-full bg-atlas-cyan/10" />
                      <div className="h-3 w-full bg-white/10" />
                      <div className="h-3 w-5/6 bg-white/10" />
                    </div>
                  )}
                  {hyps.length === 0 && !analyzing && (
                    <p className="text-[11px] font-mono text-atlas-secondary/75">
                      No hypothesis. Only unexplained anomalies are eligible (Separation of Logic).
                    </p>
                  )}
                  {hyps.slice(0, 1).map((h) => (
                    <HypBlock key={h.uid} h={h} />
                  ))}
                </div>

                {obs.explanation_note && (
                  <div
                    className={`mb-4 p-2.5 border text-[11px] font-mono leading-relaxed ${
                      obs.triage_status === "unexplained_anomaly"
                        ? "border-atlas-red/40 text-atlas-red/90"
                        : "border-atlas-orange/40 text-atlas-orange/90"
                    }`}
                  >
                    <span className="uppercase tracking-widest text-[9px] block mb-1 opacity-70">Triage Verdict</span>
                    {obs.explanation_note}
                    {event && (
                      <div className="mt-1.5 text-atlas-secondary">
                        Linked event: {event.title} ({event.category}) · {event.provider}
                      </div>
                    )}
                  </div>
                )}
                {obs.playbook && <PlaybookBlock playbook={obs.playbook} testId={`drawer-playbook-${obs.uid}`} />}
                {obs.cascade && <CascadeChain cascade={obs.cascade} testId={`drawer-cascade-${obs.uid}`} />}
              </Col>

              <Col>
                {obs.impact && <ImpactBlock impact={obs.impact} testId={`drawer-impact-${obs.uid}`} />}
                {obs.exposure && (
                  <ExposureBlock exposure={obs.exposure} variant="full" testId={`drawer-exposure-${obs.uid}`} />
                )}
                <div className="mb-4">
                  <Label>Provenance Envelope</Label>
                  <Row k="Source Agency" v={p.source} />
                  <Row k="Instrument" v={p.instrument} />
                  <Row k="Product" v={p.product} />
                  <Row k="Spatial Res." v={p.spatial_resolution} />
                  <Row k="Method" v={p.measurement_method} />
                  <Row k="Observed" v={(p.observed_at || "").replace("T", " ").slice(0, 19) + "Z"} />
                  <Row k="Retrieved" v={(p.retrieved_at || "").replace("T", " ").slice(0, 19) + "Z"} />
                  <Row k="Data Class" v={p.data_class} />
                </div>
                <div className="mb-4">
                  <Label>Measurement</Label>
                  <Row k="Coordinates" v={`${obs.lat}, ${obs.lon}`} />
                  <Row k="Measured" v={`${obs.value} ${obs.unit || ""}`} />
                  <Row k="Baseline μ" v={obs.seasonal_baseline_mean} />
                  <Row k="Baseline σ" v={obs.seasonal_baseline_std} />
                  {obs.metrics &&
                    Object.entries(obs.metrics)
                      .filter(([k, v]) => k !== "trend" && (v === null || typeof v !== "object"))
                      .slice(0, 9)
                      .map(([k, v]) => <Row key={k} k={k.replace(/_/g, " ")} v={String(v)} />)}
                </div>
              </Col>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
